import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  exchangeCodeForTokenAndClaims,
  logout as authLogout,
  refreshToken,
  ssoRedirectUrl,
} from '@provider/api/auth'
import { debug, log } from '@util/log'
import useAnalytics from '@hooks/useAnalytics'
import useLocalState from '@hooks/useLocalState'
import { secondToMillis } from '@util/time'
import pkce from '@provider/Login/authVerifierPair'
import { mapResponseAuth } from '@provider/context/AuthContext/util'
import { PROVIDERS } from '../../../../route.const'
import { LogoutMode } from '@hooks/analytics/typings'
import { useQuery } from 'react-query'
import { loadProvider } from '@provider/api/providerProfile'
import useUser from '@context/UserContext/useUser'
import { ProviderModel } from '@provider/typing'
import { BaseAuthContext } from '@common/Login/typing'
import { defaultAuthContext } from '@common/Login/constant'
import useIdle from '@common/Login/useIdle'
import localStore from '@service/local-store'
import { useLocation, useSearchParams } from 'react-router-dom'
import useNavigateWithParams from '@hooks/useNavigate'
// import LoadingError from '@common/LoadingError'

export interface Auth {
  exp: number
  displayName: string
  email: string
  access: string[]
  sub: string
  expires_in: number
  refresh_token: string
  token_fetched_at: number
}

interface User {
  name: string
  email: string
  access: string[]
  msid: string
}

export interface IAuthContext extends BaseAuthContext<User> {}

const authContext: IAuthContext = defaultAuthContext

// eslint-disable-next-line
let intervalId: NodeJS.Timeout | null = null
const ProviderAuthContext = createContext<IAuthContext>(authContext)

export const useAuth = () => useContext(ProviderAuthContext)

export const ProviderAuthProvider: FC<PropsWithChildren> = ({ children }) => {
  const [auth, setAuth] = useLocalState<Auth | null>('auth-data', null)
  const { setUser } = useUser<ProviderModel>()
  const [searchParams] = useSearchParams()
  const code = searchParams.get('code')
  const location = useLocation()
  const navigate = useNavigateWithParams()
  const { trackLogout } = useAnalytics()
  const [error, setError] = useState<string | null>(null)
  const authorizingRef = useRef<boolean>(false)
  const processedCodeRef = useRef<string | null>(null)

  const { data, refetch, isError, isLoadingError } = useQuery(
    ['provider-details'],
    loadProvider,
    {
      enabled: false,
    }
  )

  const isValidRequest = () => {
    return (
      localStore.get('lastLogin') === null ||
      localStore.get('lastLogin') === '' ||
      localStore.get('lastLogin') === PROVIDERS
    )
  }

  useEffect(() => {
    if (data && isValidRequest()) {
      setUser(data, PROVIDERS)
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const isLoggedIn = useMemo(() => {
    return auth?.token_fetched_at
      ? auth.token_fetched_at + secondToMillis(auth.expires_in) >= Date.now()
      : false
  }, [auth])

  useEffect(() => {
    if (isLoggedIn && isValidRequest()) {
      refetch()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn])

  const user = useMemo(() => {
    return auth
      ? {
          name: auth.displayName,
          email: auth.email,
          access: auth.access,
          msid: auth.sub,
        }
      : null
  }, [auth])

  const login = async () => {
    const loginEvent = localStore.get('loginEvent')
    if (loginEvent) {
      if (!isLoggedIn && !code) {
        setAuth(null)
        const challenge = await pkce.getChallenge()
        const { data } = await ssoRedirectUrl(challenge)
        localStore.set('route', location.pathname)
        localStore.get('is_register_page') === 'true'
          ? window.location.replace(data.login + '&prompt=create')
          : window.location.replace(data.login)
        localStore.set('is_register_page', 'false')
      }
    } else {
      navigate(`/`)
    }
    localStore.remove('loginEvent')
  }

  const authorize = async () => {
    // Prevent duplicate authorization calls
    if (authorizingRef.current) {
      log('Authorization already in progress, skipping duplicate call')
      return { login: false, stopNavigate: true }
    }

    // Check if this code has already been processed
    if (code && processedCodeRef.current === code) {
      log('Code has already been processed, skipping duplicate authorization')
      return { login: false, stopNavigate: true }
    }

    if (!auth) {
      if (!isLoggedIn && code && isValidRequest()) {
        // Check if verifier exists before proceeding
        const authPair = localStore.get('auth-pair')
        if (!authPair || !authPair.verifier) {
          log(
            'No PKCE verifier found, authorization already completed or invalid state'
          )
          return { login: false, stopNavigate: true }
        }

        authorizingRef.current = true
        processedCodeRef.current = code

        try {
          const verifier = pkce.getVerifier()
          const { data } = await exchangeCodeForTokenAndClaims(code, verifier)
          localStore.remove('route')
          if (!data.error) {
            const authData = mapResponseAuth(data)
            setAuth({ ...authData })
            window.history.replaceState({}, document.title, '/')
            return { login: true, stopNavigate: false }
          } else {
            setError(data.error)
            window.history.replaceState({}, document.title, '/')
            throw new Error(data.error)
          }
        } finally {
          authorizingRef.current = false
        }
      }
    }
    return { login: false, stopNavigate: true }
  }

  const logout = async (mode: LogoutMode) => {
    if (auth) {
      await authLogout()
      trackLogout(PROVIDERS, mode)
    }
    setAuth(null)
    if (intervalId) {
      clearInterval(intervalId)
    }
    localStore.remove('lastLogin')
  }

  const triggerRefresh = async (auth: Auth) => {
    try {
      debug('Refreshing token')
      const { data } = await refreshToken(auth.refresh_token)
      if (auth && !data.error && isValidRequest()) {
        const authData = mapResponseAuth(data)
        const updatedAuth: Auth = {
          ...authData,
          exp: Math.floor(Date.now() / 1000) + auth.expires_in,
          token_fetched_at: Date.now(),
        }
        setAuth({ ...updatedAuth })
      } else if (data.error) {
        throw new Error(data.error)
      }
    } catch (error) {
      debug('Token refresh failed', error)
      logout('timeout')
    }
  }

  useEffect(() => {
    if (auth && isValidRequest()) {
      const timeInMilliSeconds =
        auth.token_fetched_at + secondToMillis(auth.expires_in) - Date.now()
      debug(`token expires in :: ${timeInMilliSeconds / 1000}`)
      if (timeInMilliSeconds > 0) {
        if (intervalId) {
          debug('clearing interval for active login')
          clearInterval(intervalId)
        }
        intervalId = setTimeout(() => {
          debug(`setting interval for active login`)
          triggerRefresh(auth)
        }, timeInMilliSeconds)
      }
    }
    return () => {
      if (intervalId) {
        debug('clearing interval for cleanup')
        clearInterval(intervalId)
      }
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth])

  const { start, pause } = useIdle(logout)

  useEffect(() => {
    if (auth) {
      debug('starting idle timer')
      start()
    }
    return () => {
      debug('pausing idle timer')
      pause()
    }
    //eslint-disable-next-line
  }, [auth, start, pause])

  useEffect(() => {
    if (isError || isLoadingError) {
      setError('Error loading the Data')
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError, isLoadingError])

  return (
    <ProviderAuthContext.Provider
      value={{
        user,
        isLoggedIn,
        login,
        authorize,
        loginError: error,
        logout,
      }}
    >
      {children}
    </ProviderAuthContext.Provider>
  )
}
